import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../../component/appLayout';
import { Group, TaskFormation } from './styles';
import Accounting from '../../../component/accounting';
import TaskViewSwitch from './taskViewSwitch';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { SHOW_TASK_GROUPING_TYPE, STATUS_TYPE, TASK_LIST_TYPE } from '../../../global/constants';
import { getTaskGroupList } from '../../../services/taskServices';
import { getCurrentProjectDetails, getMilestoneList } from '../../../services/projectServices';
import { captureException } from '../../../services/logService';
import { rootStore } from '../../../mobx/rootStore';
import { syncTasks } from '../../../services/syncService';
import { observer } from 'mobx-react-lite';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { TaskModel } from '../../../mobx/models/task';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import Modal from '../../../component/modals/modal';
import CreateTaskModalV2 from '../../../component/modals/createTaskModal';
import { formatFirebaseFilter, mergeTaskFilter } from '../../../mobx/common';
import { ProjectPreferenceData } from '../../../mobx/stores/projectStore';
import { ROUTES } from '../../../routes/urls';

type TaskCountAccumulator = {
  completedTaskCount: number;
  completedSubTaskCount: number;
  currentCompletedSubTasksCount: number;
  uncompletedParentTaskCount: number;
  deletedParentTaskCount: number;
  openParentTaskIds: Set<string>;
  allParentTaskIds: Set<string>;
  pendingCompletedSubTaskCountsByParentId: Map<string, number>;
};

type SubTaskCounts = {
  currentCompletedSubTasksCount: number;
  uncompletedParentTaskCount: number;
  deletedParentTaskCount: number;
};

const Tasks: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);
  const [hiddenTask, setHiddenTask] = useState<number>(0);
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);

  //Mobx store
  const { projectStore, workspaceStore } = rootStore;
  const { currentProject: projectData } = projectStore;
  const { currentWorkspace: workspace } = workspaceStore;
  // Other variables
  const params: { id: string } = useParams();
  const history = useHistory();
  const location = useLocation();
  const taskId = new URLSearchParams(location?.search).get('task');

  const calculateTaskCount = useCallback(() => {
    // Get base task lists
    const projectTasks = projectData?.tasks || ([] as TaskModel[]);
    const filteredProjectTasks = projectData?.filteredProjectTasks?.filteredTasks || [];

    // Get filtered task statuses
    const cancelledTasks =
      filteredProjectTasks.find((taskGroup) => taskGroup.status === STATUS_TYPE.CANCELLED)?.tasks || [];
    const currentCompletedParentTasks =
      filteredProjectTasks.find((taskGroup) => taskGroup.status === STATUS_TYPE.COMPLETED)?.tasks || [];

    // Create completed parent task id set for fast lookup
    const completedParentTaskIds = new Set<string>(
      currentCompletedParentTasks.map((task) => task.id).filter((taskId): taskId is string => Boolean(taskId))
    );

    // Single pass reduce over all project tasks
    const taskCountAccumulator = projectTasks.reduce<TaskCountAccumulator>(
      (currentAccumulator, task) => {
        // Parent task
        if (!task.parentTaskId) {
          currentAccumulator.allParentTaskIds.add(task.id);

          if (!task.status) {
            currentAccumulator.openParentTaskIds.add(task.id);
          }

          if (task.status && task.statusId) {
            currentAccumulator.completedTaskCount += 1;
          }

          // Resolve previously seen completed subtasks whose parent appears later in the list
          const pendingCompletedSubTaskCount =
            currentAccumulator.pendingCompletedSubTaskCountsByParentId.get(task.id) || 0;

          if (pendingCompletedSubTaskCount > 0) {
            if (completedParentTaskIds.has(task.id)) {
              currentAccumulator.currentCompletedSubTasksCount += pendingCompletedSubTaskCount;
            } else if (!task.status) {
              currentAccumulator.uncompletedParentTaskCount += pendingCompletedSubTaskCount;
            }

            currentAccumulator.pendingCompletedSubTaskCountsByParentId.delete(task.id);
          }

          return currentAccumulator;
        }

        // Completed subtask
        if (task.status && task.statusId) {
          currentAccumulator.completedSubTaskCount += 1;

          if (completedParentTaskIds.has(task.parentTaskId)) {
            currentAccumulator.currentCompletedSubTasksCount += 1;
          } else if (currentAccumulator.openParentTaskIds.has(task.parentTaskId)) {
            currentAccumulator.uncompletedParentTaskCount += 1;
          } else if (!currentAccumulator.allParentTaskIds.has(task.parentTaskId)) {
            const existingPendingCount =
              currentAccumulator.pendingCompletedSubTaskCountsByParentId.get(task.parentTaskId) || 0;

            currentAccumulator.pendingCompletedSubTaskCountsByParentId.set(task.parentTaskId, existingPendingCount + 1);
          }
        }

        return currentAccumulator;
      },
      {
        completedTaskCount: 0,
        completedSubTaskCount: 0,
        currentCompletedSubTasksCount: 0,
        uncompletedParentTaskCount: 0,
        deletedParentTaskCount: 0,
        openParentTaskIds: new Set<string>(),
        allParentTaskIds: new Set<string>(),
        pendingCompletedSubTaskCountsByParentId: new Map<string, number>()
      }
    );

    // Any remaining pending subtasks belong to deleted or missing parent tasks
    const remainingPendingSubTaskCounts = Array.from(
      taskCountAccumulator.pendingCompletedSubTaskCountsByParentId.values()
    ).reduce((totalCount, pendingCount) => totalCount + pendingCount, 0);

    const subTaskCounts: SubTaskCounts = {
      currentCompletedSubTasksCount: taskCountAccumulator.currentCompletedSubTasksCount,
      uncompletedParentTaskCount: taskCountAccumulator.uncompletedParentTaskCount,
      deletedParentTaskCount: remainingPendingSubTaskCounts
    };

    // Calculate final counts
    const completedTaskTotal =
      taskCountAccumulator.completedTaskCount + taskCountAccumulator.completedSubTaskCount - cancelledTasks.length;

    const hiddenTaskCount =
      completedTaskTotal -
      currentCompletedParentTasks.length -
      subTaskCounts.currentCompletedSubTasksCount -
      subTaskCounts.uncompletedParentTaskCount -
      subTaskCounts.deletedParentTaskCount;

    setHiddenTask(hiddenTaskCount);
  }, [projectData?.filteredProjectTasks?.filteredTasks, projectData?.tasks]);

  useEffect(() => {
    if (
      projectData?.taskFilterType?.taskViewType === TASK_LIST_TYPE.TASKS &&
      projectData?.taskFilterType?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS
    ) {
      calculateTaskCount();
    }
  }, [calculateTaskCount, projectData?.taskFilterType?.groupingStatus, projectData?.taskFilterType?.taskViewType]);

  const loadTaskData = useCallback(async () => {
    const [projectDetails] = await Promise.all([
      getCurrentProjectDetails(params?.id),
      syncTasks(),
      getTaskGroupList(params?.id, false, false)
    ]);

    // Read from the store (not the projectData dep) so setting the current project doesn't recreate
    // this callback and re-trigger loadData's effect.
    if (rootStore.projectStore.currentProject?.id !== params?.id) {
      rootStore.projectStore.setCurrentProject(params?.id);
    }
    if (projectDetails?.isMilestone) {
      getMilestoneList(params?.id, false, false);
    }
    const currentUser = UserPreferenceSingleton.getInstance().getCurrentUser();
    const validUser = projectDetails?.users?.find((user) => user?.id === currentUser?.id);
    if (!validUser) {
      history.push(ROUTES.INBOX);
      return;
    }
    return projectDetails;
  }, [history, params?.id]);

  const loadFilterData = useCallback(
    (projectPreference: ProjectPreferenceData) => {
      const currentProject = rootStore.projectStore.projects.find((project) => project?.id === params?.id);
      const firebaseFilter = formatFirebaseFilter(projectPreference?.tasks_filter_type);
      const newFilter = mergeTaskFilter(firebaseFilter, currentProject?.taskFilterType);
      currentProject?.setTaskFilterType(newFilter);
    },
    [params?.id]
  );

  const loadDisplayPropertiesFilter = useCallback(
    (projectPreference: ProjectPreferenceData) => {
      const currentProject = rootStore.projectStore.projects.find((project) => project?.id === params?.id);
      const task_list_properties = projectPreference?.list_items_display_filter || {};
      const newFilter = { ...(currentProject?.listItemsDisplayFilter || {}), ...(task_list_properties || {}) };
      currentProject?.setListItemsDisplayFilter(newFilter);
    },
    [params?.id]
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (!taskId) {
        if (workspace?.id) {
          // loadTaskData already fetched the project preference doc inside getCurrentProjectDetails;
          // reuse it here instead of reading the same Firestore doc a second time.
          const projectDetails = await loadTaskData();
          const projectPreference = projectDetails?.projectPreference || {};
          loadFilterData(projectPreference);
          loadDisplayPropertiesFilter(projectPreference);
        }
      } else {
        history.push(ROUTES.TASK_DETAILS.replace(':id', params?.id || '').replace(':taskId', taskId));
      }
      setLoading(false);
    } catch (e) {
      captureException(e);
      setLoading(false);
    }
  }, [taskId, workspace?.id, params?.id, history, loadDisplayPropertiesFilter, loadFilterData, loadTaskData]);

  const onChangeFilterType = useCallback(async () => {
    if (workspace?.id && params?.id) {
      await loadTaskData();
    }
  }, [loadTaskData, params?.id, workspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const createTaskModel = useCallback(async () => {
    if (projectData?.id) {
      rootStore.taskStore.createTaskInput.setProject(projectData?.id);
      setIsCreateTaskOpen(true);
    }
  }, [projectData]);

  const handleCancel = useCallback(() => {
    setIsCreateTaskOpen(false);
    rootStore.taskStore.createTaskInput.clear();
  }, []);

  const startOfTheWeek = useMemo(() => projectData?.startOfTheWeek || new Date(), [projectData?.startOfTheWeek]);

  return (
    <>
      <AppLayout>
        <Group>
          <TaskFormation>
            <Accounting otherTaskList={onChangeFilterType} loadTaskList={loadData} isProjectTasks={true} />
            <TaskViewSwitch
              taskViewType={projectData?.taskFilterType?.taskViewType || TASK_LIST_TYPE.TASKS}
              loading={loading}
              loadData={loadData}
              hiddenTask={hiddenTask}
              startOfTheWeek={startOfTheWeek}
            />
          </TaskFormation>
        </Group>
      </AppLayout>
      {/* bottom navigation  */}
      <BottomSubNavigation
        backNavigationPath='/home'
        rightElement={<BottomNavigationItem iconName='plus-icon' onClick={createTaskModel} />}
      />
      <Modal open={isCreateTaskOpen} onClose={handleCancel} isCreateTask={true}>
        <CreateTaskModalV2 loadData={loadData} onCancel={handleCancel} />
      </Modal>
    </>
  );
};

export default observer(Tasks);
